import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import { 
  Folder, 
  File, 
  Download, 
  ArrowLeft, 
  RefreshCw, 
  Loader,
  FolderOpen,
  FileText,
  Image,
  FileArchive,
  Music,
  Video,
  FileCode
} from 'lucide-react';
import { formatDate } from '@/lib/utils';

interface FileItem {
  name: string;
  type: 'file' | 'directory';
  size?: number;
  sizeFormatted?: string;
  modifiedAt: string;
  path: string;
  relativePath: string;
}

interface FilesResponse {
  files: FileItem[];
  currentPath: string;
  basePath: string;
  absolutePath?: string;
}

export const DownloadsPage: React.FC = () => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [currentPath, setCurrentPath] = useState<string>('');
  const [absolutePath, setAbsolutePath] = useState<string>('');
  const [basePath, setBasePath] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [pathHistory, setPathHistory] = useState<string[]>([]);

  useEffect(() => {
    loadFiles();
  }, [currentPath]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ success: boolean; data: FilesResponse }>('/downloads/list', {
        params: { folder: currentPath },
      });
      
      if (response.data.success) {
        setFiles(response.data.data.files);
        setAbsolutePath(response.data.data.absolutePath || '');
        setBasePath(response.data.data.basePath || '');
      } else {
        toast.error('Erro ao carregar arquivos');
      }
    } catch (error: any) {
      console.error('Erro ao carregar arquivos:', error);
      toast.error(error.response?.data?.error || 'Erro ao carregar arquivos');
    } finally {
      setLoading(false);
    }
  };

  const handleFolderClick = (folder: FileItem) => {
    setPathHistory([...pathHistory, currentPath]);
    // Usar relativePath se disponível, senão usar o nome da pasta
    if (folder.relativePath) {
      setCurrentPath(folder.relativePath);
    } else {
      // Construir novo caminho
      const newPath = currentPath 
        ? `${currentPath}/${folder.name}`.replace(/\/+/g, '/')
        : folder.name;
      setCurrentPath(newPath);
    }
  };

  const handleBackClick = () => {
    if (pathHistory.length > 0) {
      const previousPath = pathHistory[pathHistory.length - 1];
      setPathHistory(pathHistory.slice(0, -1));
      setCurrentPath(previousPath);
    } else {
      setCurrentPath('');
    }
  };

  const handleDownload = async (file: FileItem) => {
    try {
      setDownloading(file.name);
      
      const response = await api.get('/downloads/download', {
        params: { file: file.relativePath },
        responseType: 'blob',
      });

      // Criar URL do blob e fazer download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Download de ${file.name} iniciado`);
    } catch (error: any) {
      console.error('Erro ao fazer download:', error);
      toast.error(error.response?.data?.error || 'Erro ao fazer download');
    } finally {
      setDownloading(null);
    }
  };

  const getFileIcon = (file: FileItem) => {
    if (file.type === 'directory') {
      return <FolderOpen className="h-8 w-8 text-blue-500" />;
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    const iconClass = "h-8 w-8";

    switch (ext) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return <Image className={`${iconClass} text-green-500`} />;
      case 'pdf':
        return <FileText className={`${iconClass} text-red-500`} />;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return <FileArchive className={`${iconClass} text-yellow-500`} />;
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'm4a':
        return <Music className={`${iconClass} text-purple-500`} />;
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'mkv':
        return <Video className={`${iconClass} text-pink-500`} />;
      case 'csv':
      case 'xlsx':
      case 'xls':
        return <FileCode className={`${iconClass} text-green-600`} />;
      default:
        return <File className={`${iconClass} text-gray-500`} />;
    }
  };

  const getBreadcrumbPath = () => {
    if (!currentPath && !absolutePath) return 'Raiz do Projeto';
    if (absolutePath) {
      // Mostrar caminho absoluto se disponível
      return absolutePath;
    }
    return currentPath.split('/').filter(Boolean).join(' / ') || 'Raiz';
  };

  const handleGoToRoot = () => {
    setPathHistory([]);
    setCurrentPath('');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Downloads</h1>
          <p className="text-muted-foreground mt-1">
            Navegue e baixe arquivos do servidor
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleGoToRoot}
            disabled={!currentPath && pathHistory.length === 0}
          >
            <Folder className="h-4 w-4 mr-2" />
            Raiz
          </Button>
          <Button
            variant="outline"
            onClick={handleBackClick}
            disabled={!currentPath && pathHistory.length === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <Button
            variant="outline"
            onClick={loadFiles}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Breadcrumb */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Folder className="h-4 w-4" />
              <span className="font-medium">Caminho Atual:</span>
            </div>
            <div className="text-sm font-mono bg-muted p-2 rounded break-all">
              {getBreadcrumbPath()}
            </div>
            {basePath && (
              <div className="text-xs text-muted-foreground">
                Base: <span className="font-mono">{basePath}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lista de Arquivos */}
      <Card>
        <CardHeader>
          <CardTitle>Arquivos e Pastas</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Folder className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum arquivo ou pasta encontrada</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {files.map((file) => (
                <div
                  key={file.relativePath}
                  className={`
                    border rounded-lg p-4 hover:bg-muted/80 transition-colors cursor-pointer
                    ${file.type === 'directory' ? 'hover:border-primary' : ''}
                  `}
                  onClick={() => file.type === 'directory' && handleFolderClick(file)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        {getFileIcon(file)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium truncate" title={file.name}>
                          {file.name}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(file.modifiedAt, 'dd/MM/yyyy HH:mm')}
                        </p>
                        {file.type === 'file' && file.sizeFormatted && (
                          <p className="text-xs text-muted-foreground">
                            {file.sizeFormatted}
                          </p>
                        )}
                      </div>
                    </div>
                    {file.type === 'file' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(file);
                        }}
                        disabled={downloading === file.name}
                        className="flex-shrink-0"
                      >
                        {downloading === file.name ? (
                          <Loader className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                  {file.type === 'directory' && (
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-xs text-muted-foreground">
                        Clique para abrir
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
